import DOMPurify from 'dompurify'

/** Caminho da API que serve as imagens enviadas pelo editor da nota (`GET /notas-versao/imagens/{id}`). */
export const CAMINHO_IMAGENS_NOTA_VERSAO = '/notas-versao/imagens/'
const ID_IMAGEM = /^[0-9a-f]{32}\.(png|jpg|webp)$/

/**
 * Só é aceita como `src` a imagem servida pela própria API (mesma origem de `REACT_APP_API_URL`, ou da página
 * quando a API é relativa) sob o caminho das imagens de nota: fora `data:` (base64 inflando o HTML que vai a
 * todos os usuários) e qualquer domínio externo (rastreamento por IP de quem abre a nota).
 */
export const ehImagemDaApi = (src: string, apiUrl: string): boolean => {
  let url: URL
  let base: URL
  try {
    base = new URL(`${apiUrl}${CAMINHO_IMAGENS_NOTA_VERSAO}`, window.location.origin)
    url = new URL(src, window.location.origin)
  } catch {
    return false
  }
  if (url.origin !== base.origin || !url.pathname.startsWith(base.pathname)) return false
  return ID_IMAGEM.test(url.pathname.slice(base.pathname.length))
}

const ATRIBUTOS_DE_RECURSO = new Set(['src', 'srcset', 'poster'])

const criarSanitizador = (apiUrl: string) => {
  const purificador = DOMPurify(window)
  purificador.setConfig({
    USE_PROFILES: { html: true },
    // Mídia que carrega recurso externo fora; só `<img>` entra, e só da API.
    FORBID_TAGS: ['picture', 'source', 'track', 'video', 'audio'],
    FORBID_ATTR: ['srcset', 'poster'],
  })
  purificador.addHook('uponSanitizeAttribute', (node, dados) => {
    if (!ATRIBUTOS_DE_RECURSO.has(dados.attrName)) return
    if (
      node.nodeName === 'IMG' &&
      dados.attrName === 'src' &&
      ehImagemDaApi(dados.attrValue, apiUrl)
    )
      return
    dados.keepAttr = false
  })
  // `<img>` que perdeu o `src` não tem o que mostrar: sai inteira, em vez de sobrar um ícone de imagem quebrada.
  purificador.addHook('afterSanitizeAttributes', (node) => {
    if (node.nodeName === 'IMG' && !node.getAttribute('src')) node.remove()
  })
  return purificador
}

const sanitizadores = new Map<string, ReturnType<typeof criarSanitizador>>()

export const sanitizarNotaVersao = (
  html: string,
  apiUrl: string = process.env.REACT_APP_API_URL ?? ''
): string => {
  let sanitizador = sanitizadores.get(apiUrl)
  if (!sanitizador) {
    sanitizador = criarSanitizador(apiUrl)
    sanitizadores.set(apiUrl, sanitizador)
  }
  return sanitizador.sanitize(html)
}
